
















































import {
  computed,
  defineComponent,
  nextTick,
  ref,
  watch
} from "@vue/composition-api";

import { isOnView, range } from "@/utils";
import { CodeMatch } from "@/utils/encoding";
import { Document } from "@/types/documents";
import { ConceptMatch } from "@/utils/analysis";

import BaseTab from "@/components/BaseTab.vue";
import NavigablePane from "@/components/NavigablePane.vue";
import MatchedDocument from "@/components/msa/MatchedDocument.vue";
import TimelineContainer from "@/components/timeline/TimelineContainer.vue";

interface Props {
  results: Document;
  selected: any;
  type: string;
}

export default defineComponent<Props>({
  name: "DocumentAnalysis",
  components: {
    MatchedDocument,
    NavigablePane,
    BaseTab,
    TimelineContainer
  },
  props: {
    results: { type: Object },
    selected: { type: Object },
    type: { type: String, default: () => "1" }
  },
  setup(props: Props) {
    const types = [
      ["1", "Conditions & Symptoms"],
      ["7", "Procedures"],
      ["35", "Diag. Procedures"],
      ["4", "Labs & Vitals"],
      ["6", "Medications"],
      ["28", "Devices"]
    ];

    const scrollable = ref<HTMLElement | null>(null);

    const conceptsByTokenIndex = (matches: any) => {
      const result: ConceptMatch[][] = new Array(props.results.tokens.length)
        .fill(null)
        .map(() => []);

      for (let match of matches) {
        for (let text of match.concept.texts || [match.concept.text]) {
          for (let index = text.tokpos[0]; index < text.tokpos[1]; index++) {
            result[index].push(match);
          }
        }
      }

      return result;
    };

    const codesByTokenIndex = computed(() => {
      const result: CodeMatch[][] = new Array(props.results.tokens.length)
        .fill(null)
        .map(() => []);

      for (let item of props.results.items) {
        if (item.type === "match") {
          for (let position of item.match.positions) {
            result[position].push(item.match);
          }
        }
      }

      return result;
    });

    const onlyRemaining = (concepts: ConceptMatch[][], codes: CodeMatch[][]) =>
      concepts.map((cluster: ConceptMatch[] | null, index: number) => {
        if (
          !cluster ||
          cluster.length === 0 ||
          cluster[0].context["assertion"] !== "posited" ||
          cluster[0].context["subject"] !== "patient" ||
          cluster[0].context["allergy"] === "true" ||
          codes.value[index].length > 0 ||
          range(...cluster[0].concept.text.tokpos).some(
            (j: number) => codes.value[j].length > 0
          )
        )
          return null;

        return cluster;
      });

    const matchesIndex = computed(() => {
      return Object.fromEntries(
        Object.entries(props.results.matches).map(([type, matches]) => [
          type,
          conceptsByTokenIndex(matches)
        ])
      );
    });

    const flatten = computed(() =>
      [...Object.values(props.results.matches)].flat(1)
    );

    watch(
      () => props.selected,
      () => {
        nextTick(() => {
          if (!scrollable.value || !props.selected) return;

          const highlighted = Array.from<HTMLElement>(
            scrollable.value.querySelectorAll(".evidence-highlighted")
          ).sort((a, b) => a.offsetTop - b.offsetTop);

          if (
            highlighted.length > 0 &&
            !highlighted.some(highlight =>
              isOnView(scrollable.value, highlight)
            )
          )
            scrollable.value.scrollTop =
              highlighted[0].offsetTop +
              highlighted[0].parentElement.offsetTop -
              100;
        });
      }
    );

    return { types, matchesIndex, flatten, codesByTokenIndex, scrollable };
  }
});
